class DooScienceDiagram extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;

    this.dataset.ready = 'true';
    this.markers = [...this.querySelectorAll('[data-doo-science-marker]')];
    this.panels = [...this.querySelectorAll('[data-doo-science-panel]')];
    this.triggers = [...this.querySelectorAll('[data-doo-science-trigger]')];
    this.visual = this.querySelector('[data-doo-science-visual]');
    this.resetButton = this.querySelector('[data-doo-science-reset]');
    this.zoom = Number.parseFloat(this.dataset.dooScienceZoom) || 1.75;

    const firstId = this.markers[0]?.dataset.dooScienceMarker;
    if (firstId) this.activateStep(firstId, { focusPad: false });

    // One delegated click path keeps marker buttons, complete accordion rows
    // and their nested + icon synchronised, including after a Theme Editor
    // block re-render. Keyboard activation of either button follows this same
    // native click path; merely tabbing through the controls changes nothing.
    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (target.closest('[data-doo-science-reset]')) {
        this.resetFocus();
        return;
      }

      const control = target.closest('[data-doo-science-marker], [data-doo-science-trigger]');
      if (!control || !this.contains(control)) return;

      const id = control.dataset.dooScienceMarker || control.dataset.dooScienceTrigger;
      if (id) this.activateStep(id, { focusPad: true });
    });
  }

  activateStep(id, { focusPad = false } = {}) {
    this.dataset.activeStep = id;

    this.markers.forEach((marker) => {
      const isActive = marker.dataset.dooScienceMarker === id;
      marker.classList.toggle('is-active', isActive);
      // aria-expanded only. Setting aria-pressed alongside it told assistive
      // tech the marker was both a disclosure and a toggle button.
      marker.setAttribute('aria-expanded', String(isActive));
    });

    this.panels.forEach((panel) => {
      const isActive = panel.dataset.dooSciencePanel === id;
      panel.classList.toggle('is-active', isActive);

      const collapse = panel.querySelector('.doo-science__callout-collapse');
      collapse?.setAttribute('aria-hidden', String(!isActive));
    });

    this.triggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(trigger.dataset.dooScienceTrigger === id));
    });

    if (focusPad) this.focusVisual(id);
  }

  focusVisual(id) {
    if (!this.visual) return;

    const marker = this.markers.find((item) => item.dataset.dooScienceMarker === id);
    if (!marker) return;

    const focusX = Number.parseFloat(marker.dataset.dooScienceFocusX);
    const focusY = Number.parseFloat(marker.dataset.dooScienceFocusY);
    const focusWidth = Number.parseFloat(marker.dataset.dooScienceFocusWidth);
    const focusHeight = Number.parseFloat(marker.dataset.dooScienceFocusHeight);
    const safeX = Number.isFinite(focusX) ? focusX : 50;
    const safeY = Number.isFinite(focusY) ? focusY : 50;
    const safeWidth = Number.isFinite(focusWidth) ? focusWidth : 24;
    const safeHeight = Number.isFinite(focusHeight) ? focusHeight : 10;

    const visualZoom = window.matchMedia('(max-width: 599px)').matches
      ? 1 + (this.zoom - 1) * 0.72
      : this.zoom;

    this.visual.style.setProperty('--doo-science-focus-x', `${safeX}%`);
    this.visual.style.setProperty('--doo-science-focus-y', `${safeY}%`);
    this.visual.style.setProperty('--doo-science-zoom', String(visualZoom));
    this.visual.style.setProperty('--doo-science-focus-width', `${safeWidth * visualZoom}%`);
    this.visual.style.setProperty('--doo-science-focus-height', `${safeHeight * visualZoom}%`);
    this.visual.dataset.focusAccent = marker.dataset.dooScienceAccent || 'shout';
    this.visual.classList.add('is-focused');

    if (this.resetButton) this.resetButton.hidden = false;
  }

  resetFocus() {
    if (!this.visual) return;

    this.visual.classList.remove('is-focused');
    delete this.visual.dataset.focusAccent;
    if (this.resetButton) this.resetButton.hidden = true;
  }
}

if (!customElements.get('doo-science-diagram')) {
  customElements.define('doo-science-diagram', DooScienceDiagram);
}
